// Site-wide behaviour: reveal-on-scroll animations (IntersectionObserver + stagger), animated
// <details> accordions, a dialog focus trap, and the "scroll the selected carousel item into
// view" helper for the Theme Editor (bespoke CSS scroll-snap carousels: mosaic, value-props).
(function () {
  'use strict';

  /* Reveal on scroll ------------------------------------------------------ */
  function animationsDisabled() {
    return (
      document.body.classList.contains('animations-off') ||
      (window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    );
  }

  /* Assegna un ritardo incrementale ai figli .reveal dei contenitori
     [data-reveal-stagger], per un ingresso a cascata. Lo step è configurabile
     con data-reveal-stagger="120" (ms); default 90ms, cap a 700ms.
     ASSUNZIONE: i .reveal dentro un gruppo stagger non devono contenere a loro
     volta altri .reveal annidati (l'indice li conterebbe). Oggi rispettata. */
  function applyStagger(root) {
    (root || document)
      .querySelectorAll('[data-reveal-stagger]')
      .forEach(function (group) {
        var step = parseInt(group.getAttribute('data-reveal-stagger'), 10);
        if (!step || step < 0) step = 90;
        var items = group.querySelectorAll('.reveal');
        for (var i = 0; i < items.length; i++) {
          items[i].style.setProperty('--reveal-delay', Math.min(i * step, 700) + 'ms');
        }
      });
  }

  var revealObserver = null;
  function getRevealObserver() {
    if (revealObserver) return revealObserver;
    revealObserver = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );
    return revealObserver;
  }

  /* root = document (load iniziale) oppure la sezione ricaricata su shopify:section:load */
  function initReveal(root) {
    root = root || document;
    // Da qui in poi è il JS a gestire la comparsa: disinnesca il failsafe CSS.
    document.documentElement.classList.add('reveal-ready');
    var els = root.querySelectorAll('.reveal');
    if (!els.length) return;
    // Animazioni disattivate, reveal-on-scroll spento dal Theme Editor, o reduced-motion:
    // mostra tutto subito, nessun movimento al reveal.
    if (
      animationsDisabled() ||
      document.body.classList.contains('reveal-off') ||
      !('IntersectionObserver' in window)
    ) {
      els.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }
    applyStagger(root);
    var io = getRevealObserver();
    els.forEach(function (el) {
      if (!el.classList.contains('is-visible')) io.observe(el);
    });
  }

  /* <details> accordion: close siblings + animate the panel height -------- */
  function animateDetails(details, panel, opening) {
    if (details._accAnimating) return;
    details._accAnimating = true;
    // Measure BEFORE writing any inline style (the panel is at its natural state: on open the
    // caller has just flipped details.open to true). Panels carry their own vertical padding,
    // and with border-box the used height can't fall below the padding sum -- so we must animate
    // padding-top/padding-bottom together with height, otherwise the close stalls at the padding
    // stub for the tail of the duration (looks frozen) then snaps shut at open = false.
    var cs = window.getComputedStyle(panel);
    var padT = parseFloat(cs.paddingTop) || 0;
    var padB = parseFloat(cs.paddingBottom) || 0;
    var fullH = panel.scrollHeight; // border-box: content + padding
    panel.style.overflow = 'hidden';
    panel.style.height = (opening ? 0 : fullH) + 'px';
    panel.style.paddingTop = (opening ? 0 : padT) + 'px';
    panel.style.paddingBottom = (opening ? 0 : padB) + 'px';
    panel.getBoundingClientRect(); // force reflow so the transition starts from the initial state
    panel.style.transition =
      'height var(--duration-medium) var(--ease-settle),' +
      ' padding-top var(--duration-medium) var(--ease-settle),' +
      ' padding-bottom var(--duration-medium) var(--ease-settle)';
    panel.style.height = (opening ? fullH : 0) + 'px';
    panel.style.paddingTop = (opening ? padT : 0) + 'px';
    panel.style.paddingBottom = (opening ? padB : 0) + 'px';
    function done(ev) {
      // Only the PANEL's own height transition ends the animation: the padding transitionends
      // (three properties transition now) and any bubbling up from inner elements are ignored.
      if (ev && (ev.propertyName !== 'height' || ev.target !== panel)) return;
      panel.removeEventListener('transitionend', done);
      clearTimeout(details._accTimer);
      panel.style.transition = '';
      panel.style.height = '';
      panel.style.paddingTop = '';
      panel.style.paddingBottom = '';
      panel.style.overflow = '';
      details._accAnimating = false;
      if (!opening) details.open = false;
    }
    panel.addEventListener('transitionend', done);
    // Fallback in case transitionend never fires (e.g. zero-height panel)
    details._accTimer = setTimeout(function () { done(); }, 600);
  }

  function accPanel(details) {
    var summary = details.querySelector(':scope > summary');
    return summary ? summary.nextElementSibling : null;
  }
  function accShouldAnimate(panel) {
    if (!panel || animationsDisabled()) return false;
    var pos = window.getComputedStyle(panel).position;
    return pos !== 'absolute' && pos !== 'fixed';
  }
  // Some accordions neutralise their summary via CSS at certain breakpoints (tech-specs on
  // desktop, and in its "grid" mobile mode: `.specs__card-head { pointer-events: none }`) so the
  // panel stays permanently visible regardless of [open]. pointer-events:none blocks real
  // pointer clicks before they ever reach us, but a keyboard Enter/Space on a focused <summary>
  // still synthesises a click event (and, if left unhandled, a native toggle) -- so without this
  // guard a keyboard user on desktop would trigger animateDetails()/sibling-closing on a panel
  // that has no visible open/closed state, producing a flash-collapse-then-return with no real
  // state change. Detected generically off the live computed style (not a hardcoded breakpoint
  // or class) so any future accordion using the same neutralise-via-pointer-events pattern is
  // covered automatically.
  function accIsInert(details) {
    var summary = details.querySelector(':scope > summary');
    return !!summary && window.getComputedStyle(summary).pointerEvents === 'none';
  }

  function initAccordions() {
    // Close other open <details> in the same group. Animate the collapse when the panel
    // is a static (non-floating) accordion, so the sibling folds shut instead of snapping.
    document.addEventListener('toggle', function (event) {
      var el = event.target;
      if (!(el instanceof HTMLDetailsElement) || !el.open) return;
      var group = el.getAttribute('data-accordion-group');
      if (!group || accIsInert(el)) return;
      document
        .querySelectorAll('details[data-accordion-group="' + group + '"]')
        .forEach(function (other) {
          if (other === el || !other.open || other._accAnimating) return;
          var op = accPanel(other);
          if (accShouldAnimate(op)) {
            animateDetails(other, op, false); // sets open=false when finished
          } else {
            other.open = false;
          }
        });
    }, true);

    // Animate open/close height for static (non-floating) accordion panels.
    // Floating panels (desktop mega, position:absolute/fixed) keep their native
    // toggle + CSS opacity animation, so we skip them here.
    document.addEventListener('click', function (e) {
      var summary = e.target.closest && e.target.closest('summary');
      if (!summary) return;
      var details = summary.parentElement;
      if (!details || details.tagName !== 'DETAILS' || !details.hasAttribute('data-accordion-group')) return;
      if (accIsInert(details)) return; // CSS-neutralised summary (e.g. tech-specs desktop/grid):
      // let the native (unanimated) toggle happen -- a true no-op, since the panel's forced
      // display:block doesn't depend on [open].
      var panel = summary.nextElementSibling;
      if (!accShouldAnimate(panel)) return; // reduced motion / master off / floating -> native toggle
      if (details._accAnimating) { e.preventDefault(); return; }
      e.preventDefault();
      if (details.open) {
        animateDetails(details, panel, false); // sets open=false when finished
      } else {
        details.open = true; // fires toggle -> collapses siblings in the group
        animateDetails(details, panel, true);
      }
    });
  }

  /* Fetch the up-to-date cart bubble count -------------------------------- */
  window.HendyCart = {
    getSectionsUrl: function () {
      return window.Shopify && window.Shopify.routes
        ? window.Shopify.routes.root
        : '/';
    },
    updateBubble: function (count) {
      document.querySelectorAll('[data-cart-count]').forEach(function (node) {
        node.textContent = count;
        node.hidden = count === 0;
      });
    }
  };

  /* Focus trap for open modal dialogs -------------------------------------- */
  function visibleDialog() {
    var dialogs = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
    for (var i = 0; i < dialogs.length; i++) {
      if (dialogs[i].offsetParent !== null) return dialogs[i];
    }
    return null;
  }

  function initFocusTrap() {
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var dialog = visibleDialog();
      if (!dialog) return;
      // The mobile nav drawer runs its own dedicated focus trap (HendyMobileNav in
      // component-mega-menu.js), which knows about its inert inactive screen. Left to itself, this
      // generic trap filters focusables only by offsetParent, which stays non-null for an inert
      // screen translated off to the side — it can then compute a boundary element inside that
      // inert screen, preventDefault, and have the browser silently refuse to focus it, so Tab does
      // nothing. Bail out here and leave the drawer entirely to its own trap; the search modal and
      // the cart drawer still rely on this generic one and are unaffected.
      if (dialog.closest('.mobile-nav')) return;
      var focusables = Array.prototype.slice
        .call(dialog.querySelectorAll(
          'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
        ))
        .filter(function (el) {
          return el.offsetParent !== null;
        });
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  ready(function () {
    // initReveal per primo e in try/catch: un errore in altre init non deve
    // lasciare invisibile il contenuto con .reveal.
    try { initReveal(); } catch (e) {}
    try { initAccordions(); } catch (e) {}
    try { initFocusTrap(); } catch (e) {}
  });

  // Theme Editor: le sezioni ri-renderizzate via AJAX vanno re-inizializzate,
  // altrimenti i nuovi elementi .reveal restano a opacity:0.
  document.addEventListener('shopify:section:load', function (e) {
    try { initReveal(e.target); } catch (_) {}
  });

  // Theme Editor: i caroselli mobile bespoke sono scroll-snap puro (nessun custom element) — il
  // mosaico e la riga dei punti di forza. Quando il merchant seleziona un block dalla sidebar,
  // portalo in vista dentro il suo contenitore orizzontale — come fa carousel-grid per le proprie
  // slide — così l'anteprima non resta "cieca". Ogni item aderisce con [data-carousel-item]; la sua
  // track è il genitore diretto. La guardia scrollWidth>clientWidth fa scattare lo scroll solo
  // quando è davvero un carosello (viewport mobile), non quando lo stesso DOM è una griglia su desktop.
  document.addEventListener('shopify:block:select', function (e) {
    var item = e.target && e.target.closest ? e.target.closest('[data-carousel-item]') : null;
    if (!item) return;
    var track = item.parentElement;
    if (!track || track.scrollWidth <= track.clientWidth) return;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    item.scrollIntoView({ inline: 'start', block: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
  });

  // Theme Editor: le card manuali di tech-specs sono <details data-accordion-group> come gli altri
  // accordion del tema -- selezionando una card diversa dalla prima nella sidebar, forzala aperta
  // (stesso pattern del block-select/-deselect su .product__collapsible/.featured-product__collapsible
  // in product.js), altrimenti in anteprima mobile resterebbe chiusa. Vive qui e non in product.js
  // perché global.js è sempre caricato, mentre product.js lo è solo se la pagina ha un prodotto (e
  // tech-specs non è vincolata al template prodotto nello schema). Le card da metaoggetto (percorso
  // dynamic source) non portano `shopify_attributes`, quindi non sono mai target di questo evento.
  document.addEventListener('shopify:block:select', function (e) {
    var details = e.target && e.target.closest ? e.target.closest('.specs__card') : null;
    if (details) details.open = true;
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var details = e.target && e.target.closest ? e.target.closest('.specs__card') : null;
    if (details) details.open = false;
  });
})();
